import { clsx } from 'clsx'
import { Suspense, lazy, useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import {
  DropdownMenu,
  DropdownMenuDivider,
  DropdownMenuItem,
  DropdownMenuLink,
} from ':/_console/js/components/DropdownMenu/DropdownMenu.tsx'
import { useWorkspaceSlug } from '~/api/swr/useWorkspaceSlug.ts'
import { Badge } from '~/components/Badge.tsx'
import { Text } from '~/components/Text.tsx'
import Tooltip from '~/components/Tooltip/index.tsx'
import { useWorkspace } from '~/features/workspaces/data.ts'
import { useSwitchToWorkspace } from '~/utils/useSwitchToWorkspace.ts'

import styles from './WorkspaceSelect.module.scss'
import { OpenModalButton } from '../../Modal/components/OpenModalButton.tsx'

const CreateNewWorkspaceModal = lazy(() =>
  import('~/features/templates/components/CreateNewWorkspaceModal.tsx').then((m) => ({
    default: m.CreateNewWorkspaceModal,
  })),
)

function WorkspaceSelect() {
  const { data } = useWorkspace()
  const { getNextWorkspacePath, saveWorkspaceToLocalStorage } = useSwitchToWorkspace(
    data?.login_data.isImpersonated,
  )

  const [open, setOpen] = useState(false)

  const { slug, isLoading } = useWorkspaceSlug()

  const workspaces = useMemo(
    () =>
      data?.memberships
        .map((x) => ({
          app_icon_color: x.app_icon_color,
          ...x.workspace,
        }))
        .sort((a, b) => {
          if (!a.cancelled && b.cancelled) {
            return -1
          }
          if (a.cancelled && !b.cancelled) {
            return 1
          }

          return a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })
        }) ?? [],
    [data?.memberships],
  )

  const currentWorkspace = workspaces.find((x) => x.slug === slug)

  // close the dropdown when the route changes
  const location = useLocation()
  useEffect(() => {
    setOpen(false)
  }, [location])

  if (isLoading || !currentWorkspace) {
    return (
      <div className={styles['workspace-menu']}>
        <button
          type="button"
          className={clsx(
            `btn`,
            styles[`workspace-menu__link`],
            styles[`workspace-menu__link--toggler`],
            {
              [styles['workspace-menu__link--active']]: open,
            },
          )}
          onClick={() => setOpen(!open)}
        >
          <span className={clsx(styles['workspace-icon'], styles[`workspace-icon--placeholder`])}>
            {slug?.charAt(0).toUpperCase()}
          </span>
          {slug || <span className="placeholder">loading…</span>}
        </button>
      </div>
    )
  }

  return (
    <div className={styles['workspace-menu']}>
      <Tooltip
        type="dropdown"
        arrow={false}
        placement="bottom-start"
        className={styles['workspace-dropdown']}
        onClickOutside={() => setOpen(false)}
        visible={open}
        content={
          <DropdownMenu className={styles['dropdown-menu']}>
            {workspaces.map((w) => (
              <DropdownMenuItem key={w.id}>
                <DropdownMenuLink
                  asChild
                  className={clsx(
                    styles[`workspace-menu__link`],
                    styles[`workspace-menu__link--menu-item`],
                    {
                      [styles['workspace-menu__link--active']]: w.id === currentWorkspace.id,
                    },
                  )}
                >
                  <Link
                    to={getNextWorkspacePath(w.slug)}
                    onClick={() => saveWorkspaceToLocalStorage(w.slug)}
                  >
                    {/* workspace-icon - N className indicates the number of the app. Every app number (up to 10) has its own color. */}
                    <span
                      className={clsx(
                        styles['workspace-icon'],
                        styles[`workspace-icon--${w.app_icon_color}` as keyof typeof styles],
                      )}
                    >
                      {w.name.charAt(0).toUpperCase()}
                    </span>
                    <span
                      className={clsx('app-menu__name', {
                        'app-menu__name-cancelled': w.cancelled,
                      })}
                    >
                      <Text mb={0} color={w.cancelled ? 'muted' : undefined}>
                        {w.name}
                      </Text>

                      {w.cancelled && (
                        <Text size="sm" mb={0} color="muted">
                          <Badge variant="neutral">Cancelled</Badge>
                        </Text>
                      )}
                    </span>
                  </Link>
                </DropdownMenuLink>
              </DropdownMenuItem>
            ))}

            <DropdownMenuDivider />

            <DropdownMenuItem>
              <Suspense
                fallback={
                  <Link
                    to="/c/create-workspace"
                    className={clsx(
                      'btn--unstyled',
                      styles[`workspace-menu__link`],
                      styles[`workspace-menu__link--menu-item`],
                      styles['workspace-menu__link--add'],
                    )}
                  >
                    Create new Workspace
                  </Link>
                }
              >
                <CreateNewWorkspaceModal
                  toggleComponent={
                    <DropdownMenuLink
                      asChild
                      className={clsx(
                        'btn--unstyled',
                        styles[`workspace-menu__link`],
                        styles[`workspace-menu__link--menu-item`],
                        styles['workspace-menu__link--add'],
                      )}
                    >
                      <OpenModalButton>Create new Workspace</OpenModalButton>
                    </DropdownMenuLink>
                  }
                />
              </Suspense>
            </DropdownMenuItem>
          </DropdownMenu>
        }
      >
        <button
          type="button"
          className={clsx(
            `btn`,
            styles[`workspace-menu__link`],
            styles[`workspace-menu__link--toggler`],
            {
              [styles['workspace-menu__link--active']]: open,
            },
          )}
          onClick={() => setOpen(!open)}
        >
          <span
            className={clsx(
              styles['workspace-icon'],
              styles[`workspace-icon--${currentWorkspace.app_icon_color}` as keyof typeof styles],
            )}
          >
            {currentWorkspace.name.charAt(0).toUpperCase()}
          </span>
          <span className={styles['workspace-menu__name']}>{currentWorkspace.name}</span>
        </button>
      </Tooltip>
    </div>
  )
}

export { WorkspaceSelect }
